<template>
  <v-layout
    wrap
    pl-1
    pr-1
    pt-4
  >
    <v-flex
      v-if="!$route.params.id"
      x12
    >
      <v-select
        v-model.trim="form.dataroom.exchange_id"
        :items="supportedExchanges"
        :item-text="'name'"
        :item-value="'id'"
        :error-messages="dataroomNameErrors"
        name="dataroom-name"
        :label="$t('accounts.exchange')+'*'"
        required
        @change="changeDataroom"
        @blur="$v.form.dataroom.exchange_id.$touch()"
      />
    </v-flex>
    <v-flex
      v-if="exchangeQuoteAssets && selectedExchange.quote_asset_mandatory"
      xs12
    >
      <v-autocomplete
        v-model.trim="form.dataroom.quote_asset"
        :items="exchangeQuoteAssets.symbols"
        :error-messages="dataroomQuoteAssetErrors"
        :label="$t('globals.quoteAsset')+'*'"
        :disabled="$apollo.loading"
        name="dataroom-quote-asset"
        required
        @blur="$v.form.dataroom.quote_asset.$touch()"
      />
    </v-flex>
    <v-flex
      v-if="exchangeMarkets && selectedExchange.markets_mandatory"
      xs12
    >
      <v-autocomplete
        v-model.trim="form.dataroom.markets"
        :items="exchangeMarkets.symbols"
        :error-messages="dataroomMarketsErrors"
        :label="$t('globals.supportedMarkets')+'*'"
        name="dataroom-markets"
        :disabled="$apollo.loading"
        chips
        multiple
        required
        @blur="$v.form.dataroom.markets.$touch()"
      >
        <template v-slot:append>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            {{ $t('globals.marketsHint') }}
          </v-tooltip>
        </template>
      </v-autocomplete>
    </v-flex>
    <v-flex
      xs12
      md12
    >
      <v-simple-table
        v-if="$route.params.id != undefined"
      >
        <thead>
          <tr>
            <th class="text-left">
              {{ $t('projects.dataRoom') }}
            </th>
            <th class="text-left">
              {{ $t('accounts.key') }}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="text-primary">
              <b>{{ form.dataroom.name }}</b>
            </td>
            <td v-if="form.dataroom.apikey">
              {{ form.dataroom.apikey | obfuscate(5) }}
            </td>
            <td class="text-right">
              <v-btn
                outlined
                text
                small
                @click="editDataroom()"
              >
                {{ $t('projects.editButton') }}
              </v-btn>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-flex>
    <v-flex
      v-if="($route.params.id && editable) || !$route.params.id"
      xs12
      md12
    >
      <v-text-field
        id="dataroom-apikey"
        v-model.trim="form.dataroom.apikey"
        :error-messages="dataroomApikeyErrors"
        name="dataroom-apikey"
        :label="$t('accounts.key')+'*'"
        required
        @input="$v.form.dataroom.apikey.$touch()"
        @blur="$v.form.dataroom.apikey.$touch()"
      />

      <v-text-field
        id="dataroom-secret"
        v-model.trim="form.dataroom.secret"
        :error-messages="dataroomSecretErrors"
        name="dataroom-secret"
        :label="$t('accounts.secret')+'*'"
        required
        @input="$v.form.dataroom.secret.$touch()"
        @blur="$v.form.dataroom.secret.$touch()"
      />
    </v-flex>
    <v-flex
      v-if="!$route.params.id"
    >
      <v-checkbox
        v-model="acceptTerms"
        color="primary"
      >
        <template v-slot:label>
          <b>
            {{ $t('terms.iAccept') }}
            <a
              :href="`https://static.aivia.io/docs/aivia-trader-agreement-${$i18n.locale}.pdf`"
              target="_blank"
              @click.stop
            >
              {{ $t('terms.acceptTrader') }}
            </a>
          </b>
          <p
            v-if="error !== null"
            class="text-danger"
          >
            <small>{{ error }}</small>
          </p>
        </template>
      </v-checkbox>
    </v-flex>
  </v-layout>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import EXCHANGES_QUERY from "@/graphql/queries/SupportedExchanges.gql";
import QUOTE_ASSETS_QUERY from "@/graphql/queries/ExchangeQuoteAssets.gql";
import MARKETS_QUERY from "@/graphql/queries/ExchangeMarkets.gql";
import { mapMutations } from "vuex";

export default {
  name: "FormDataroom",
  mixins: [validationMixin],
  data() {
    return {
      selectedExchange: null,
      datarooms: [],
      initForm: null,
      editable: false,
      valid: false,
      error: null,
      acceptTerms: false,
      form: {
        dataroom: {
          quote_asset: null,
          markets: [],
          apikey: null,
          secret: null,
          exchange_id: null
        }
      }
    };
  },
  validations() {
    return {
      form: this.rules
    };
  },
  computed: {
    rules() {
      var form = {
        dataroom: {
          exchange_id: {
            required
          },
          quote_asset: {
            required
          },
          markets: {
            required
          },
          apikey: {
            required
          },
          secret: {
            required
          }
        }
      };
      if (this.$route.params.id || (this.selectedExchange && !this.selectedExchange.quote_asset_mandatory)) {
         delete(form.dataroom.quote_asset);
      }
      if (this.selectedExchange && !this.selectedExchange.markets_mandatory) {
         delete(form.dataroom.markets);
      }
      if (this.$route.params.id && !this.editable) {
         delete(form.dataroom.apikey);
         delete(form.dataroom.secret);
      }
      return form;
    },
    dataroomNameErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.exchange_id.$dirty) return errors;
      !this.$v.form.dataroom.exchange_id.required &&
        errors.push(this.$t("accounts.reqExchange"));
      return errors;
    },
    dataroomApikeyErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.apikey.$dirty) return errors;
      !this.$v.form.dataroom.apikey.required &&
        errors.push(this.$t("accounts.reqKey"));
      return errors;
    },
    dataroomQuoteAssetErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.quote_asset.$dirty) return errors;
      !this.$v.form.dataroom.quote_asset.required &&
        errors.push(this.$t("globals.quoteAssetRequired"));
      return errors;
    },
    dataroomMarketsErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.markets.$dirty) return errors;
      !this.$v.form.dataroom.markets.required &&
        errors.push(this.$t("globals.marketsRequired"));
      return errors;
    },
    dataroomSecretErrors() {
      const errors = [];
      if (!this.$v.form.dataroom.secret.$dirty) return errors;
      !this.$v.form.dataroom.secret.required &&
        errors.push(this.$t("accounts.reqSecret"));
      return errors;
    }
  },
  watch: {
    form: {
      handler(value) {
        const baseCurrency = !this.selectedExchange.quote_asset_mandatory ? this.selectedExchange.base_currency : this.form.dataroom.quote_asset
        this.$emit('onChangeExchange', baseCurrency)
      },
      deep: true
    },
  },
  mounted() {
    
    if (!this.$route.params.id) {
      this.editable = true;
    }
    
    this.initForm = this.form;
    this.$store.subscribe((mutation, state) => {
      if (mutation.type == "project/setProject") {
        let dataroom = state.project.datarooms[0];
        this.form.dataroom.name = dataroom.exchange.name;
        this.form.dataroom.apikey = dataroom.apikey;
        this.form.dataroom.secret = dataroom.secret;
        this.form.dataroom.exchange_id = dataroom.exchange.id
        this.form.dataroom.markets = dataroom.markets
        this.form.dataroom.quote_asset = state.project.form.base_currency
        this.changeDataroom()
      }
    });
  },
  methods: {
    ...mapMutations("project", ["saveDataRoom"]),
    changeDataroom() {
      this.selectedExchange = this._.find(this.supportedExchanges, {
        id: this.form.dataroom.exchange_id
      });
    },
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }
      if (this.$route.params.id) {
        delete(this.form.dataroom.quote_asset)
        delete(this.form.dataroom.exchange_id)
      }
      if (this.$route.params.id && !this.editable) {
        delete(this.form.dataroom.apikey)
        delete(this.form.dataroom.secret)
      }
      if (!this.selectedExchange.markets_mandatory) {
        delete(this.form.dataroom.markets)
      }
      this.saveDataRoom(this.form);
    },
    editDataroom() {
      this.editable = !this.editable;
    }
  },
  apollo: {
    supportedExchanges: {
      query: EXCHANGES_QUERY,
    },
    exchangeQuoteAssets: {
      query: QUOTE_ASSETS_QUERY,
      variables() {
        return {
           exchange_id: this.form.dataroom.exchange_id
        }
      },
      skip() {
        return this.$route.params.id || !this.selectedExchange || !this.selectedExchange.quote_asset_mandatory
      }
    },
    exchangeMarkets: {
      query: MARKETS_QUERY,
      variables() {
        return {
           exchange_id: this.form.dataroom.exchange_id,
           quote_asset: this.form.dataroom.quote_asset
        }
      },
      skip() {
        return !this.form.dataroom.quote_asset
      }
    }
  }
};
</script>

<style>
</style>


